import React, { useEffect, useState } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import { changeExpiredPassword } from '../api'

type LocationState = {
  email?: string
  challengeToken?: string
}

const schema = Yup.object().shape({
  currentPassword: Yup.string().required('Current password is required'),
  newPassword: Yup.string()
    .required('New password is required')
    .min(8, 'Password must be between 8 and 128 characters.')
    .max(128, 'Password must be between 8 and 128 characters.')
    .matches(/[A-Z]/, 'Must contain at least one uppercase letter')
    .matches(/[a-z]/, 'Must contain at least one lowercase letter')
    .matches(/[0-9]/, 'Must contain at least one number')
    .matches(/[\^$*.[\]{}()?\-"!@#%&/,><':;|_~`]/, 'Must contain at least one special character'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword')], 'Passwords must match')
    .required('Confirm password is required'),
})

const initialValues = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
}

const PasswordExpired: React.FC = () => {
  const history = useHistory()
  const location = useLocation<LocationState | undefined>()
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const email = location.state?.email ?? ''
  const challengeToken = location.state?.challengeToken ?? ''

  useEffect(() => {
    if (!email || !challengeToken) {
      history.replace('/auth/login')
    }
  }, [challengeToken, email, history])

  const formik = useFormik({
    initialValues,
    validationSchema: schema,
    onSubmit: async (values, { setSubmitting, setStatus, resetForm }) => {
      setStatus(undefined)
      try {
        await changeExpiredPassword(
          email,
          values.currentPassword,
          values.newPassword,
          values.confirmPassword,
          challengeToken
        )
        resetForm()
        history.replace('/auth/login')
      } catch (error: any) {
        const message =
          error?.response?.data?.message ??
          error?.response?.data?.Message ??
          'Something went wrong. Please try again.'
        setStatus(message)
      } finally {
        setSubmitting(false)
      }
    },
  })

  return (
    <form className='form w-100' onSubmit={formik.handleSubmit} noValidate>
      <div className='text-center mb-10'>
        <h1 className='text-dark mb-3'>Password Expired</h1>
        <div className='text-gray-500 fw-bold fs-6'>
          Your password has expired. Set a new password to continue.
        </div>
      </div>

      {formik.status && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold text-danger'>{formik.status}</div>
        </div>
      )}

      <div className='fv-row mb-10'>
        <label className='form-label fs-6 fw-bolder text-dark'>Email</label>
        <input
          className='form-control form-control-lg form-control-solid'
          type='email'
          value={email}
          disabled
          readOnly
        />
      </div>

      <div className='fv-row mb-10'>
        <label className='form-label fs-6 fw-bolder text-dark'>Current Password</label>
        <div className='position-relative'>
          <input
            placeholder='Current Password'
            type={showCurrentPassword ? 'text' : 'password'}
            autoComplete='current-password'
            {...formik.getFieldProps('currentPassword')}
            className={clsx(
              'form-control form-control-lg form-control-solid',
              { 'is-invalid': formik.touched.currentPassword && formik.errors.currentPassword },
              { 'is-valid': formik.touched.currentPassword && !formik.errors.currentPassword }
            )}
          />
          <button
            type='button'
            className='btn btn-sm btn-light position-absolute top-50 end-0 translate-middle-y me-2'
            onClick={() => setShowCurrentPassword((value) => !value)}
            aria-label={showCurrentPassword ? 'Hide current password' : 'Show current password'}
          >
            <i className={`bi ${showCurrentPassword ? 'bi-eye-slash-fill' : 'bi-eye-fill'}`}></i>
          </button>
        </div>
        {formik.touched.currentPassword && formik.errors.currentPassword && (
          <div className='fv-plugins-message-container'>
            <span role='alert' className='text-danger'>
              {formik.errors.currentPassword}
            </span>
          </div>
        )}
      </div>

      <div className='fv-row mb-10'>
        <label className='form-label fs-6 fw-bolder text-dark'>New Password</label>
        <div className='position-relative'>
          <input
            placeholder='New Password'
            type={showNewPassword ? 'text' : 'password'}
            autoComplete='new-password'
            {...formik.getFieldProps('newPassword')}
            className={clsx(
              'form-control form-control-lg form-control-solid',
              { 'is-invalid': formik.touched.newPassword && formik.errors.newPassword },
              { 'is-valid': formik.touched.newPassword && !formik.errors.newPassword }
            )}
          />
          <button
            type='button'
            className='btn btn-sm btn-light position-absolute top-50 end-0 translate-middle-y me-2'
            onClick={() => setShowNewPassword((value) => !value)}
            aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}
          >
            <i className={`bi ${showNewPassword ? 'bi-eye-slash-fill' : 'bi-eye-fill'}`}></i>
          </button>
        </div>
        {formik.touched.newPassword && formik.errors.newPassword && (
          <div className='fv-plugins-message-container'>
            <span role='alert' className='text-danger'>
              {formik.errors.newPassword}
            </span>
          </div>
        )}
      </div>

      <div className='fv-row mb-10'>
        <label className='form-label fs-6 fw-bolder text-dark'>Confirm Password</label>
        <div className='position-relative'>
          <input
            placeholder='Confirm Password'
            type={showConfirmPassword ? 'text' : 'password'}
            autoComplete='new-password'
            {...formik.getFieldProps('confirmPassword')}
            className={clsx(
              'form-control form-control-lg form-control-solid',
              { 'is-invalid': formik.touched.confirmPassword && formik.errors.confirmPassword },
              { 'is-valid': formik.touched.confirmPassword && !formik.errors.confirmPassword }
            )}
          />
          <button
            type='button'
            className='btn btn-sm btn-light position-absolute top-50 end-0 translate-middle-y me-2'
            onClick={() => setShowConfirmPassword((value) => !value)}
            aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
          >
            <i className={`bi ${showConfirmPassword ? 'bi-eye-slash-fill' : 'bi-eye-fill'}`}></i>
          </button>
        </div>
        {formik.touched.confirmPassword && formik.errors.confirmPassword && (
          <div className='fv-plugins-message-container'>
            <span role='alert' className='text-danger'>
              {formik.errors.confirmPassword}
            </span>
          </div>
        )}
      </div>

      <div className='text-center'>
        <button
          type='submit'
          className='btn btn-lg btn-primary w-100 mb-5'
          disabled={
            formik.isSubmitting ||
            !formik.isValid ||
            !formik.values.currentPassword ||
            !formik.values.newPassword ||
            !formik.values.confirmPassword
          }
        >
          {!formik.isSubmitting && <span className='indicator-label'>Update Password</span>}
          {formik.isSubmitting && (
            <span className='indicator-progress' style={{ display: 'block' }}>
              Please wait...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
        <Link to={`/auth/forgot-password?email=${encodeURIComponent(email)}`} className='link-primary fw-bolder'>
          Forgot your password?
        </Link>
      </div>
    </form>
  )
}

export { PasswordExpired }
